import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { FiChevronRight } from "react-icons/fi";
import { FaFileInvoice } from "react-icons/fa6";
import { LuDownload, LuCheck } from "react-icons/lu";
import { formatDateDDMMMYYYY } from "../../utils/dateHelpers";
import { createPayment } from "../../utils/createPayment";
import { showToast } from "../../utils/toaster";
import SEO from "../../components/SEO";
import { validateDiscountCoupon } from "../../utils/validateDiscountCoupon";
import { HiOutlineArrowSmRight } from "react-icons/hi";

const OrderDetails = () => {
  const location = useLocation();

  // Retrieve the quotation data from the state passed during navigation.
  const { quotation } = location.state || {};

  // 1) Local states for coupon
  const [couponInput, setCouponInput] = useState(quotation?.couponCode || "");
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // 2) Update coupon code & validate with API
  const handleUpdateCouponCode = async () => {
    if (!couponInput) {
      showToast("Please enter a coupon code", "error");
      return;
    }
    try {
      const couponResponse = await validateDiscountCoupon(couponInput);
      if (couponResponse.status === 1) {
        // If coupon is valid, store it in local state
        setAppliedCoupon(couponResponse);
        showToast(couponResponse.message || "Coupon code applied", "success");
      } else {
        // If coupon is invalid, clear appliedCoupon
        setAppliedCoupon(null);
        showToast(
          couponResponse.message || "Invalid or expired coupon",
          "error"
        );
      }
    } catch (err) {
      setAppliedCoupon(null);
      showToast("Failed to validate coupon", "error");
    }
  };

  const allProductsPrice =
    quotation && quotation.quotationDetails
      ? quotation.quotationDetails.reduce(
          (acc, detail) => acc + parseFloat(detail.totalProductPrice),
          0
        )
      : 0;

  // 4) Determine which discount to use:
  //    - If the user applied a new valid coupon, use that.
  //    - Else, fallback to the original quotation.discountPercentage
  const discountPercentage =
    appliedCoupon && appliedCoupon.status === 1
      ? appliedCoupon.percentage // from validateDiscountCoupon
      : quotation?.discountPercentage
      ? parseFloat(quotation.discountPercentage)
      : 0;

  // GST Calculation Logic:
  const sellingPrice = allProductsPrice;
  const discountAmount = sellingPrice * (discountPercentage / 100);
  const preGSTTotal = sellingPrice - discountAmount;
  const gstAmount = preGSTTotal * 0.18; // 18% GST

  // Shipping charges
  const shippingCharges = quotation?.shippingCharges
    ? parseFloat(quotation.shippingCharges)
    : 0;

  // Final grand total: preGSTTotal + (18% GST) + shipping
  const grandTotal = preGSTTotal * 1.18 + shippingCharges;

  // Define order statuses (timeline steps) and parse the current status.
  const statuses = [
    "Order Received",
    "Order Processing",
    "Ready to Dispatch",
    "In Transit",
    "Out for Delivery",
    "Delivered",
  ];

  const currentStatus = parseInt(quotation.quoteStatus, 10);

  // -------------------------------
  // Handle Checkout Payment Function
  // -------------------------------
  const handleCheckout = async () => {
    if (!quotation) {
      showToast("No quotation found", "error");
      return;
    }

    // Build product info by joining the product names.
    const productInfo =
      quotation.quotationDetails && quotation.quotationDetails.length > 0
        ? quotation.quotationDetails.map((item) => item.productName).join(", ")
        : "Product";

    // Use quotation.totalPrice as the amount (assumed to be GST inclusive).
    const amount = parseFloat(quotation.totalPrice).toFixed(2);

    // Build the checkoutData payload.
    const checkoutData = {
      amount: amount,
      quotationNumber: quotation.quotationNumber,
      productInfo: productInfo,
      firstName: quotation.email.split("@")[0] || "",
      email: quotation.email,
      // Use quotationDetailID's from each quotation detail.
      productIds: quotation.quotationDetails
        .map((item) => item.quotationDetailID)
        .join(","),
      discountCoupon: quotation.couponCode || "",
    };

    // Save the checkoutData in localStorage (if needed later).
    localStorage.setItem("quotePaymentData", JSON.stringify(checkoutData));

    try {
      const result = await createPayment(checkoutData);
      if (result) {
        // Create a hidden form to submit the payment details to PayU.
        const form = document.createElement("form");
        form.method = "POST";
        form.action = result.action; // e.g. "https://test.payu.in/_payment"
        form.target = "_blank";

        addHiddenField(form, "key", result.key);
        addHiddenField(form, "txnid", result.txnId);
        addHiddenField(form, "hash", result.hash);
        addHiddenField(form, "amount", result.amount);
        addHiddenField(form, "firstname", result.firstName);
        addHiddenField(form, "email", result.email);
        addHiddenField(form, "productinfo", result.productInfo);
        addHiddenField(form, "surl", result.surl);
        addHiddenField(form, "furl", result.furl);

        document.body.appendChild(form);
        form.submit();
      }
    } catch (error) {
      showToast("Payment checkout failed", "error");
      console.error("Checkout error:", error);
    }
  };

  const addHiddenField = (form, name, value) => {
    const hiddenField = document.createElement("input");
    hiddenField.type = "hidden";
    hiddenField.name = name;
    hiddenField.value = value;
    form.appendChild(hiddenField);
  };

  return (
    <div className="min-h-screen about-us-page">
      <Header />
      <SEO
        title="Your Order Details - COLTEC Global"
        description="Review the details of your recent order with COLTEC Global. Access comprehensive order information, including shipping, tracking, and purchase history, for a seamless post-purchase experience."
        ogTitle="Order Details - COLTEC Global"
        ogDescription="Access complete details of your order including shipping, tracking, and purchase history. Experience hassle-free order management with COLTEC Global."
        ogImage="https://content.qeeb.in/coltec/products/banner-product-three.webp"
        ogUrl="https://coltec-global.com/products/order-details"
        canonicalUrl="https://coltec-global.com/products/order-details"
        ogImageAlt="COLTEC Global Order Details"
        ogSiteName="COLTEC Global"
        ogType="website"
        twitterSite="https://x.com/ColtecGlobal"
        twitterTitle="Your Order Details - COLTEC Global"
        twitterDescription="Review all the specifics of your recent order, from tracking to purchase history, with COLTEC Global."
        twitterImage="https://content.qeeb.in/coltec/products/banner-product-three.webp"
        twitterCard="summary"
        geoRegion="IN"
      />

      <div className="px-6 lg:px-8 lg:pb-14 pb-10 pt-24 max-w-7xl m-auto">
        {/* Breadcrumb Navigation */}
        <nav className="flex mb-4 text-xs font-medium" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/shop" className="text-gray-900 hover:text-blue-600">
                Home
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <FiChevronRight className="text-sm text-gray-500" />
                <Link
                  to="/products/orders"
                  className="ml-1 text-gray-900 hover:text-blue-600 md:ml-2"
                >
                  My Orders
                </Link>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <FiChevronRight className="text-sm text-gray-500" />

                <span className="ml-1 text-gray-500 md:ml-2">
                  {quotation.quotationNumber}
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Product Details */}
        <div className="grid grid-cols-12 gap-6">
          <div className="lg:col-span-8 col-span-full space-y-6">
            {quotation.quotationDetails &&
              quotation.quotationDetails.map((detail) => {
                // Use detail.panelSize if available, otherwise try to extract a number (e.g. "65" or "75") from the product name.
                const panelSize =
                  detail.panelSize || detail.productName.match(/\d+/)?.[0];

                return (
                  <div
                    key={detail.quotationDetailID}
                    className="border rounded-lg overflow-hidden shadow hover:shadow-lg transition p-6"
                  >
                    <div className="flex lg:flex-row flex-col-reverse justify-between">
                      <div className="mt-4 lg:mt-0">
                        <h3 className="md:text-base text-sm font-semibold mb-1">
                          {detail.productName}
                        </h3>
                        <div className="flex space-x-2 mb-[2px] items-center">
                          <p className="text-sm font-medium">
                            <span className="text-sm text-gray-500 font-normal">
                              Quantity:
                            </span>{" "}
                            {detail.quantity}
                          </p>
                          <span className="text-gray-400">|</span>
                          {panelSize && (
                            <p className="text-sm font-medium">
                              <span className="text-sm text-gray-500 font-normal">
                                Panel Size:
                              </span>{" "}
                              {panelSize}
                            </p>
                          )}
                        </div>
                        <p className="text-sm font-medium mb-[2px]">
                          <span className="text-sm text-gray-500 font-normal">
                            Price Per Unit:
                          </span>{" "}
                          {detail.pricePerUnit}
                        </p>
                        <p className="text-sm font-medium">
                          <span className="text-sm text-gray-500 font-normal">
                            Total Price:
                          </span>{" "}
                          {detail.totalProductPrice}
                        </p>
                      </div>
                      <div>
                        <img
                          src={detail.imageUrl}
                          alt={detail.productName}
                          className="w-full lg:h-32 h-48 rounded-lg object-cover"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            {/* Order Tracking Timeline - Render only if trasationNumber is not null */}
            {quotation.trasationNumber && (
              <div className="bg-white shadow-md rounded-lg border border-gray-200 p-3 md:p-4 mb-4">
                <h4 className="text-sm font-medium text-gray-800 mb-4">
                  Order Tracking
                </h4>
                <div className="relative">
                  {/* Gray progress line */}
                  <div className="absolute top-3 left-5 md:left-12 w-[85%] md:w-[91%] h-[2.5px] bg-gray-200"></div>
                  {/* Blue progress line based on currentStatus */}
                  <div
                    className="absolute top-3 left-5  md:left-12 w-[85%] md:w-[91%] h-[2.5px] bg-green-600"
                    style={{
                      width: `${
                        ((currentStatus - 1) / (statuses.length - 1)) * 100
                      }%`,
                    }}
                  ></div>
                  <div className="flex justify-between flex-nowrap overflow-x-auto">
                    {statuses.map((status, index) => {
                      const stepNumber = index + 1;
                      const isCompleted = stepNumber <= currentStatus;
                      return (
                        <div
                          key={index}
                          className="flex flex-col md:items-center relative z-10"
                        >
                          <div
                            className={`w-[25px] h-[25px] rounded-full flex items-center justify-center ${
                              isCompleted
                                ? "bg-green-600 text-white"
                                : "bg-white border text-gray-900"
                            }`}
                          >
                            {isCompleted ? (
                              <LuCheck className="text-base" />
                            ) : (
                              <span className="text-xs">{stepNumber}</span>
                            )}
                          </div>
                          <p className="whitespace-nowrap pr-3 mt-2 text-[9px] md:text-xs md:text-center">
                            {status}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Order Summary */}
          <div className="lg:col-span-4 col-span-full">
            {quotation.invoiceUrl && (
              <div className="bg-white shadow-md rounded-lg p-4 border border-gray-200 mb-4">
                <Link
                  to={quotation.invoiceUrl}
                  target="_blank"
                  className="flex justify-between items-center"
                >
                  <div className="flex items-center font-medium">
                    <FaFileInvoice className="text-xl text-blue-600 mr-2" />
                    Invoice download
                  </div>
                  <div className="bg-black/10 hover:bg-black text-gray-500 transition-all duration-300 hover:text-white w-9 h-9 rounded-full flex justify-center items-center">
                    <LuDownload className="text-lg" />
                  </div>
                </Link>
              </div>
            )}

            <div className="bg-white shadow-md rounded-lg p-4 border border-gray-200 mb-4">
              <p className="text-sm font-medium">{quotation.quotationNumber}</p>
              <p className="text-sm font-medium my-1">{quotation.email}</p>
              <p className="text-sm font-medium">
                {formatDateDDMMMYYYY(quotation.createdAt)}
              </p>
            </div>
            {/* Updated Price Details Card with GST Calculation */}
            <div className="bg-white shadow-md rounded-lg border border-gray-200 ">
              <h4 className="border-b border-gray-200 font-medium py-2 px-4 text-[13px] text-gray-600">
                Price Details
              </h4>
              <div className="p-4">
                {quotation.pinCode && (
                  <div className="flex justify-between mb-1">
                    <p className="text-gray-500 text-sm">Pin Code</p>
                    <span className="text-sm font-medium">
                      {quotation.pinCode}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between mb-1">
                  <p className="text-gray-500 text-sm">Coupon Code</p>
                  {!quotation?.trasationNumber ? (
                    <div className="flex items-center mb-1">
                      <input
                        type="text"
                        className="coupon-input py-1.5 md:w-[140px] w-[140px] font-normal text-xs md:text-sm px-3 border-gray-200 border rounded-l-full"
                        placeholder="Enter coupon"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value)}
                      />

                      <button
                        onClick={handleUpdateCouponCode}
                        className="apply-coupon-button bg-blue-500 hover:bg-blue-600 rounded-r-full text-white py-1.5 px-4"
                      >
                        <HiOutlineArrowSmRight size={22} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium">{couponInput}</p>
                    </>
                  )}
                </div>

                {quotation.discountPercentage && (
                  <div className="flex justify-between mb-1">
                    <p className="text-gray-500 text-sm">Discount Percentage</p>
                    <span className="text-sm font-medium">
                      {quotation.discountPercentage}%
                    </span>
                  </div>
                )}
                <div className="flex justify-between mb-1">
                  <p className="text-gray-500 text-sm">Selling Price</p>
                  <span className="text-sm font-medium">
                    ₹{sellingPrice.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <p className="text-gray-500 text-sm">Discount Amount</p>
                  <span className="text-sm font-medium">
                    -₹{discountAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <p className="text-gray-500 text-sm">Pre GST Total</p>
                  <span className="text-sm font-medium">
                    ₹{preGSTTotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <p className="text-gray-500 text-sm">GST (18%)</p>
                  <span className="text-sm font-medium">
                    ₹{gstAmount.toFixed(2)}
                  </span>
                </div>
                {quotation.shippingCharges && (
                  <div className="flex justify-between mb-1">
                    <p className="text-gray-500 text-sm">Shipping Charges</p>
                    <span className="text-sm font-medium">
                      {quotation.shippingCharges}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center border-t border-gray-300 border-dashed pt-2 mt-2">
                  <p className="text-gray-500 text-sm">
                    Grand Total (incl. GST)
                  </p>
                  <span className="text-sm font-medium">
                    ₹{grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            {!quotation.trasationNumber && (
              <button
                onClick={handleCheckout}
                className="bg-black text-white py-2 px-8 rounded-full mt-4 w-full"
              >
                Checkout Payment
              </button>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrderDetails;
